import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { filtersSchema, type FiltersFormData } from './schema';
import type { Hub, Period, State } from '@/models/catalog/offer';
import { useOffer } from '@/packages/courses/pages/details/context/offer';

export const Filters = () => {
  const { filters, updateFilters, options, isLoading } = useOffer();

  const { control, watch, setValue, reset } = useForm<FiltersFormData>({
    defaultValues: {
      location: { state: filters.state, city: filters.city },
      hub: filters.hub,
      period: filters.period,
    },
    resolver: zodResolver(filtersSchema),
  });

  const { location, hub, period } = watch();
  const { state, city } = location;

  // Sync form when context filters change (e.g. from cheapest offer)
  useEffect(() => {
    const formMatchesContext =
      state === filters.state &&
      city === filters.city &&
      hub === filters.hub &&
      period === filters.period;

    if (!formMatchesContext) {
      reset({
        location: { state: filters.state, city: filters.city },
        hub: filters.hub,
        period: filters.period,
      });
    }
  }, [filters, reset, state, city, hub, period]);

  const selectedOptions = useMemo(
    () => ({
      state:
        options.states.find(
          (stateOption) => stateOption.abbreviation === state
        ) ?? null,
      hub:
        options.hubs.find(
          (hubOption) => hubOption.id.toString() === hub
        ) ?? null,
      period:
        options.periods.find(
          (periodOption) => periodOption.type === period
        ) ?? null,
    }),
    [options, state, hub, period]
  );

  const handleStateChange = (newState: string) => {
    setValue('location.state', newState);
    setValue('location.city', '');
    setValue('hub', '');
    setValue('period', '');
    updateFilters({ state: newState, city: '', hub: '', period: '' });
  };

  const handleCityChange = (newCity: string) => {
    setValue('location.city', newCity);
    setValue('hub', '');
    setValue('period', '');
    updateFilters({ city: newCity, hub: '', period: '' });
  };

  const handleHubChange = (newHub: string) => {
    setValue('hub', newHub);
    setValue('period', '');
    updateFilters({ hub: newHub, period: '' });
  };

  const handlePeriodChange = (newPeriod: string) => {
    setValue('period', newPeriod);
    updateFilters({ period: newPeriod });
  };

  return (
    <Stack gap={2}>
      <Controller
        control={control}
        name="location.state"
        render={() => (
          <FormControl aria-labelledby="location-state-label">
            <FormLabel id="location-state-label" htmlFor="location-state">
              Localização
            </FormLabel>
            <Autocomplete<State>
              loading={isLoading}
              options={options.states}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option.abbreviation === value.abbreviation
              }
              value={selectedOptions.state}
              onChange={(_, newValue) =>
                handleStateChange(newValue?.abbreviation ?? '')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="location-state"
                  aria-labelledby="location-state-label"
                  slotProps={{ htmlInput: params.inputProps }}
                  placeholder="Estado"
                />
              )}
            />
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="location.city"
        disabled={!state}
        render={() => (
          <Autocomplete
            disabled={!state}
            loading={isLoading}
            options={options.cities}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            value={city || null}
            onChange={(_, newValue) => handleCityChange(newValue ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                id="location-city"
                aria-label="Cidade"
                slotProps={{ htmlInput: params.inputProps }}
                placeholder="Cidade"
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name="hub"
        disabled={!city}
        render={() => (
          <FormControl aria-labelledby="hub-label">
            <FormLabel id="hub-label" htmlFor="hub">
              Polo
            </FormLabel>
            <Autocomplete<Hub>
              disabled={!city}
              loading={isLoading}
              options={options.hubs}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedOptions.hub}
              onChange={(_, newValue) =>
                handleHubChange(newValue?.id.toString() ?? '')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="hub"
                  aria-labelledby="hub-label"
                  slotProps={{ htmlInput: params.inputProps }}
                  placeholder="Selecione um polo"
                />
              )}
            />
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="period"
        disabled={!hub}
        render={() => (
          <FormControl aria-labelledby="period-label">
            <FormLabel id="period-label" htmlFor="period">
              Turno
            </FormLabel>
            <Autocomplete<Period>
              disabled={!hub}
              loading={isLoading}
              options={options.periods}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.type === value.type
              }
              value={selectedOptions.period}
              onChange={(_, newValue) =>
                handlePeriodChange(newValue?.type ?? '')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="period"
                  aria-labelledby="period-label"
                  slotProps={{ htmlInput: params.inputProps }}
                  placeholder="Selecione um turno"
                />
              )}
            />
          </FormControl>
        )}
      />
    </Stack>
  );
};
